import { Search, X, CalendarIcon, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatLocalDate } from "@/utils/dateUtils";
import { VAGA_STATUS_COLUMNS, type VagaStatus } from "@/utils/vaga-status";
import type { AgenciaOption } from "@/hooks/use-agencias";
import type { VagaDateField, VagaListFilters } from "@/hooks/use-vagas-list";
import { FreelancerAsyncCombobox } from "./FreelancerAsyncCombobox";

interface FreelancerOption {
  id: string;
  nome: string;
}

interface VagasListFiltersProps {
  searchInput: string;
  onSearchInput: (value: string) => void;
  filters: VagaListFilters;
  onPatch: (patch: Partial<VagaListFilters>) => void;
  agencias: AgenciaOption[];
  freelancer: FreelancerOption | null;
  onFreelancerChange: (option: FreelancerOption | null) => void;
  activeCount: number;
  onClear: () => void;
}

const DATE_FIELD_LABELS: Record<VagaDateField, string> = {
  cadastro: "Cadastro",
  aprovacao: "Aprovação",
  reprovacao: "Reprovação",
};

/** Converte "YYYY-MM-DD" -> Date local (meia-noite), sem off-by-one de fuso. */
function parseLocalDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  return new Date(value + "T00:00:00");
}

export function VagasListFilters({
  searchInput,
  onSearchInput,
  filters,
  onPatch,
  agencias,
  freelancer,
  onFreelancerChange,
  activeCount,
  onClear,
}: VagasListFiltersProps) {
  const toggleStatus = (status: VagaStatus, checked: boolean) => {
    const next = checked
      ? [...filters.statuses, status]
      : filters.statuses.filter((s) => s !== status);
    onPatch({ statuses: next });
  };

  const hasDateField = !!filters.dateField;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Busca */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => onSearchInput(e.target.value)}
            placeholder="Buscar cliente, agência ou id..."
            className="bg-white pl-9"
          />
        </div>

        {/* Agência */}
        <Combobox
          className="w-full sm:w-52 bg-white"
          allowCustomValue={false}
          options={[
            { value: "", label: "Todas as agências" },
            ...agencias.map((agencia) => ({ value: agencia.id, label: agencia.nome })),
          ]}
          value={filters.agenciaId}
          onValueChange={(value) => onPatch({ agenciaId: value })}
          placeholder="Filtrar por agência"
          searchPlaceholder="Buscar agência..."
          emptyText="Nenhuma agência encontrada."
        />

        {/* Freelancer */}
        <div className="w-full sm:w-56">
          <FreelancerAsyncCombobox value={freelancer} onChange={onFreelancerChange} />
        </div>

        {/* Status (multiselect) */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2 bg-white">
              <SlidersHorizontal className="h-4 w-4" />
              Status
              {filters.statuses.length > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5">
                  {filters.statuses.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="flex flex-col gap-0.5">
              {VAGA_STATUS_COLUMNS.map((status) => (
                <label
                  key={status.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <Checkbox
                    checked={filters.statuses.includes(status.id)}
                    onCheckedChange={(checked) => toggleStatus(status.id, !!checked)}
                  />
                  {status.title}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Período: um único seletor de QUAL data + intervalo */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filters.dateField ?? "none"}
          onValueChange={(value) =>
            onPatch({
              dateField: value === "none" ? null : (value as VagaDateField),
              ...(value === "none" ? { dateFrom: null, dateTo: null } : {}),
            })
          }
        >
          <SelectTrigger className="w-full sm:w-52 bg-white">
            <SelectValue placeholder="Filtrar data por..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem filtro de data</SelectItem>
            <SelectItem value="cadastro">Data de cadastro</SelectItem>
            <SelectItem value="aprovacao">Data de aprovação</SelectItem>
            <SelectItem value="reprovacao">Data de reprovação</SelectItem>
          </SelectContent>
        </Select>

        {hasDateField && (
          <>
            <DatePickerButton
              label="De"
              value={filters.dateFrom}
              onChange={(value) => onPatch({ dateFrom: value })}
            />
            <DatePickerButton
              label="Até"
              value={filters.dateTo}
              onChange={(value) => onPatch({ dateTo: value })}
            />
          </>
        )}

        {activeCount > 0 && (
          <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={onClear}>
            <X className="h-3.5 w-3.5" />
            Limpar filtros ({activeCount})
          </Button>
        )}
      </div>
    </div>
  );
}

interface DatePickerButtonProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

function DatePickerButton({ label, value, onChange }: DatePickerButtonProps) {
  const date = parseLocalDate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-start gap-2 bg-white font-normal">
          <CalendarIcon className="h-4 w-4" />
          {date ? `${label}: ${date.toLocaleDateString("pt-BR")}` : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(next) => onChange(next ? (formatLocalDate(next) ?? null) : null)}
        />
      </PopoverContent>
    </Popover>
  );
}
